<script setup lang="ts" generic="T extends Date | Date[]">
import IconCalendar from '@/domains/common/components/icons/IconCalendar.vue';
import { computed, ref, watch, nextTick } from 'vue';
import {
    VueDatePicker,
    type DatePickerMonthYearSlotProps,
    type MonthPickerMonthYearSlotProps,
    type YearPickerMonthYearSlotProps,
    type DpInputSlotProps,
    type TextInputConfig,
    type RangeConfig,
} from '@vuepic/vue-datepicker';
import { isEqual, set, format, isBefore, parse, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import type SelectOptionInterface from '@/domains/common/typescript/SelectOptionInterface';

type MonthYearSlotProps = DatePickerMonthYearSlotProps | MonthPickerMonthYearSlotProps | YearPickerMonthYearSlotProps;

// Single format used for both displaying the value and parsing keyboard input.
const DATE_FORMAT = 'dd/MM/yyyy';

const {
    modelValue = null,
    placeholder = undefined,
    name = '',
    placeholderOnly,
    showChip,
    disabled = false,
} = defineProps<{
    modelValue?: T | null;
    placeholder?: string;
    name?: string;
    placeholderOnly?: boolean;
    showChip?: boolean;
    disabled?: boolean;
}>();

const selectedDate = computed<T | null>(() => modelValue);
const selectedMonth = ref(0);
const isInputFocused = ref<boolean>(false);
const monthOptions = ref<SelectOptionInterface<number>[]>([]);
const yearOptions = ref<SelectOptionInterface<number>[]>([]);

const getDayClass = (date: Date) => {
    const classes = ['w-8 h-8 text-sm my-0 font-medium relative'];

    if (isDateSelected(date)) {
        classes.push('rounded-full bg-button-hover');
        if (isFirstInrange(date)) {
            classes.push('before:absolute before:bg-button before:w-5 before:h-8 before:z-[-1] before:-right-1');
        }
        if (isLastInrange(date)) {
            classes.push('before:absolute before:bg-button before:w-5 before:h-8 before:z-[-1] before:-left-1');
        }
    } else if (isDateInRange(date)) {
        classes.push('bg-button rounded-none');
    } else {
        classes.push('hover:rounded-full hover:bg-button');
    }

    if (date.getMonth() === selectedMonth.value) {
        classes.push('text-black');
    } else {
        classes.push('text-border');
    }
    return classes.join(' ');
};

const formatedDate = computed((): string => {
    if (placeholderOnly) {
        return '';
    }
    if (selectedDate?.value && selectedDate.value instanceof Date) {
        return format(selectedDate.value, DATE_FORMAT);
    }
    if (selectedDate?.value && Array.isArray(selectedDate.value)) {
        return selectedDate.value
            .map((date) => {
                return format(date, DATE_FORMAT);
            })
            .join(' - ');
    }
    return '';
});

const isRange = computed(() => {
    return Array.isArray(selectedDate.value);
});

// `partialRange: false` keeps the calendar open until both ends are picked (default would commit
// and close on the first date under `auto-apply`).
const rangeConfig: Partial<RangeConfig> = { partialRange: false };

// Keyboard entry only for single-date, non-disabled fields; range filters stay mouse-only.
const isTextInputEnabled = computed(() => !isRange.value && !disabled);

// text-input only keeps the field focusable and click-to-open; we do NOT delegate parsing (the
// library auto-completes partial input and emits it via `auto-apply`). Submit is handled by
// `onTextCommit`, masking/display below.
const textInputConfig: Partial<TextInputConfig> = {
    format: DATE_FORMAT,
    openMenu: 'open',
    escClose: true,
};

// Controlled input: synced to the committed value, mirrors keystrokes, reverts on invalid/uncommitted.
const displayValue = ref('');
watch(
    formatedDate,
    (value) => {
        displayValue.value = value;
    },
    { immediate: true },
);

// Date being typed (complete + valid), previewed in the calendar without committing; falls back to
// the committed single-date value for highlighting.
const previewDate = ref<Date | null>(null);
const highlightedDate = computed<Date | null>(() => {
    if (previewDate.value) {
        return previewDate.value;
    }
    return selectedDate.value instanceof Date ? selectedDate.value : null;
});

// Fed as the library model (single-date only) so the open calendar jumps to the previewed month;
// `undefined` when idle so a committed value keeps its `getDayClass` highlight.
const previewModelValue = computed<Date | undefined>(() => {
    if (!isTextInputEnabled.value) {
        return undefined;
    }
    return previewDate.value ?? undefined;
});

// Only a full, in-range dd/MM/yyyy commits. Incomplete/non-padded (`5/6/1990` → `56/19/90`) or
// out-of-range input returns null and reverts on blur.
function parseTypedDate(text: string): Date | null {
    if (text.length !== DATE_FORMAT.length) {
        return null;
    }
    const parsed = parse(text, DATE_FORMAT, new Date());
    return isValid(parsed) ? parsed : null;
}

// Auto-insert the `/` separators as digits are typed (the library can't mask a custom slot input).
function maskDate(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)].filter((part) => part.length > 0).join('/');
}

// Caret position after `digitCount` digits, skipping separators, so re-masking doesn't jump it to the end.
function caretPositionForDigits(masked: string, digitCount: number): number {
    let position = 0;
    let seen = 0;
    while (position < masked.length && seen < digitCount) {
        if (/\d/.test(masked[position])) {
            seen += 1;
        }
        position += 1;
    }
    while (position < masked.length && masked[position] === '/') {
        position += 1;
    }
    return position;
}

function onTextInput(event: Event) {
    if (!isTextInputEnabled.value) {
        return;
    }
    const inputElement = event.target as HTMLInputElement;
    const rawValue = inputElement.value;
    const digitsBeforeCaret = rawValue
        .slice(0, inputElement.selectionStart ?? rawValue.length)
        .replace(/\D/g, '').length;
    const masked = maskDate(rawValue);
    displayValue.value = masked;
    previewTypedDate(masked);
    nextTick(() => {
        const caret = caretPositionForDigits(masked, digitsBeforeCaret);
        inputElement.setSelectionRange(caret, caret);
    });
}

// Reflect a fully-typed valid date in the open calendar before Enter, without committing.
function previewTypedDate(masked: string) {
    const parsed = parseTypedDate(masked);
    previewDate.value = parsed;
    if (parsed) {
        selectedMonth.value = parsed.getMonth();
    }
}

// Commit on Enter / Tab / blur: emit a full valid date, otherwise revert to the last valid value.
function onTextCommit(closeMenu?: () => void) {
    if (!isTextInputEnabled.value) {
        return;
    }
    const parsed = parseTypedDate(displayValue.value);
    // Hand the highlight back to the committed (or reverted) value.
    previewDate.value = null;
    if (parsed === null) {
        // `formatedDate` mirrors the committed model (empty when none).
        displayValue.value = formatedDate.value;
        return;
    }
    emit('update:modelValue', parsed as T);
    closeMenu?.();
}

const startDate = computed((): Date => {
    if (Array.isArray(selectedDate.value) && selectedDate.value.length > 0) {
        return selectedDate.value[0];
    }
    if (!Array.isArray(selectedDate.value) && selectedDate?.value) {
        return selectedDate.value;
    }
    return new Date();
});

const isChipVisible = computed(() => {
    if (Array.isArray(selectedDate.value) && selectedDate.value.length > 0) {
        return showChip;
    }
    if (!Array.isArray(selectedDate.value) && selectedDate?.value) {
        return showChip;
    }
    return false;
});

const chipCount = computed(() => {
    if (Array.isArray(selectedDate.value) && selectedDate.value.length > 0) {
        return selectedDate.value.length;
    }
    if (!Array.isArray(selectedDate.value) && selectedDate?.value) {
        return 1;
    }
    return 0;
});

function handleMonthYear(params: { month: number }) {
    selectedMonth.value = params.month;
}

function isDatePickerSlot(slotProps: MonthYearSlotProps): slotProps is DatePickerMonthYearSlotProps {
    return 'updateMonthYear' in slotProps;
}

function onOpen() {
    selectedMonth.value = startDate.value.getMonth();
    isInputFocused.value = true;
}

function onClose() {
    selectedMonth.value = startDate.value.getMonth();
    isInputFocused.value = false;
}

function onDateChange(newDate: Date | Date[] | null) {
    if (Array.isArray(newDate)) {
        return;
    }
    emit('update:modelValue', newDate as T);
}

function onRangeStart(startRange: Date) {
    emit('update:modelValue', [startRange] as T);
}

function onRangeEnd(endDate: Date) {
    if (selectedDate.value && Array.isArray(selectedDate.value)) {
        const range = [...selectedDate.value, endDate].sort((a, b) => (isBefore(a, b) ? -1 : 1));
        emit('update:modelValue', range as T);
    }
}

function isDateSelected(date: Date) {
    if (highlightedDate.value) {
        return isEqual(date, set(highlightedDate.value, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }));
    }
    if (selectedDate?.value && Array.isArray(selectedDate.value)) {
        return selectedDate.value.some((currentDate) =>
            isEqual(date, set(currentDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 })),
        );
    }
    return false;
}

function isDateInRange(date: Date) {
    if (selectedDate?.value && Array.isArray(selectedDate.value) && selectedDate.value.length === 2) {
        return date > selectedDate.value[0] && date < selectedDate.value[1];
    }
    return false;
}

function isFirstInrange(date: Date) {
    if (selectedDate?.value && Array.isArray(selectedDate.value) && selectedDate.value.length === 2) {
        return isEqual(date, set(selectedDate.value[0], { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }));
    }
    return false;
}

function isLastInrange(date: Date) {
    if (selectedDate?.value && Array.isArray(selectedDate.value) && selectedDate.value.length === 2) {
        return isEqual(date, set(selectedDate.value[1], { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }));
    }
    return false;
}

function getMonthOptions(months: { value: number; text: string }[] | undefined) {
    if (monthOptions.value.length === 0 && months) {
        monthOptions.value = buildMonthYearOptions(months);
    }
    return monthOptions.value;
}

function getYearOptions(years: { value: number; text: string }[] | undefined) {
    if (yearOptions.value.length === 0 && years) {
        yearOptions.value = buildMonthYearOptions(years);
    }
    return yearOptions.value;
}

function buildMonthYearOptions(monthsYears: { value: number; text: string }[]) {
    return monthsYears.map((monthYear) => {
        return {
            value: monthYear.value,
            label: monthYear.text,
        };
    });
}

const emit = defineEmits<{
    'update:modelValue': [value: T | null];
}>();
</script>

<template>
    <VueDatePicker
        :model-value="previewModelValue"
        :input-attrs="{ clearable: false }"
        auto-apply
        :locale="fr"
        no-today
        :ui="{
            menu: 'w-auto h-auto rounded-xl shadow-shadow shadow-drop z-10 border border-border!',
            dayClass: getDayClass,
        }"
        :floating="{ arrow: false }"
        :time-config="{ enableTimePicker: false }"
        :action-row="{ showCancel: false, showPreview: false, showSelect: false, showNow: false }"
        :day-names="['L', 'M', 'M', 'J', 'V', 'S', 'D']"
        :range="isRange ? rangeConfig : false"
        :formats="{ month: 'MMMM' }"
        :start-date="startDate"
        :disabled="disabled"
        :text-input="isTextInputEnabled ? textInputConfig : false"
        @update:model-value="onDateChange"
        @range-start="onRangeStart"
        @range-end="onRangeEnd"
        @update-month-year="handleMonthYear"
        @open="onOpen"
        @closed="onClose"
    >
        <template #dp-input="slotProps: DpInputSlotProps">
            <div
                :class="[
                    'group flex items-center overflow-hidden rounded-2xl',
                    disabled && 'cursor-not-allowed bg-gray-300',
                    !disabled && 'cursor-pointer bg-white',
                ]"
            >
                <input
                    id="calendar"
                    data-test="datepicker-input"
                    :value="displayValue"
                    :name="name"
                    type="text"
                    :placeholder="placeholder"
                    :readonly="!isTextInputEnabled"
                    :disabled="disabled"
                    :class="[
                        'placeholder:text-theme-black h-10 grow appearance-none border-none pl-4 text-sm shadow-none placeholder:font-semibold',
                        !isTextInputEnabled && 'pointer-events-none',
                        disabled && 'bg-gray-300',
                    ]"
                    @input="onTextInput($event)"
                    @keydown.enter.prevent="onTextCommit(slotProps.closeMenu)"
                    @keydown.tab="onTextCommit(slotProps.closeMenu)"
                    @blur="onTextCommit()"
                    @focus="isTextInputEnabled && slotProps.onFocus()"
                />
                <IconCalendar :class="[!disabled && 'group-hover:text-button-hover']" />
                <span
                    v-if="isChipVisible"
                    class="bg-chip group-hover:bg-chip-hover mr-5 flex h-5 w-5 items-center justify-center rounded-full text-sm"
                >
                    {{ chipCount }}
                </span>
            </div>
        </template>
        <template #month-year="slotProps: MonthYearSlotProps">
            <div
                v-if="isDatePickerSlot(slotProps)"
                class="align-center relative flex w-full flex-row items-center justify-center"
            >
                <BaseSelect
                    class="w-auto"
                    mode="noShadow"
                    :model-value="slotProps.month"
                    :options="getMonthOptions(slotProps.months)"
                    @update:model-value="slotProps.updateMonthYear($event, slotProps.year, true)"
                />
                <BaseSelect
                    class="w-auto"
                    mode="noShadow"
                    :model-value="slotProps.year"
                    :options="getYearOptions(slotProps.years)"
                    @update:model-value="slotProps.updateMonthYear(slotProps.month, $event, true)"
                />
            </div>
        </template>
        <template #calendar-header="{ day }">
            <div class="h-7 w-8 p-1.5 text-sm font-bold">
                {{ day }}
            </div>
        </template>
    </VueDatePicker>
</template>

<style>
/* stylelint-disable selector-class-pattern */
.dp--main {
    width: auto;
    font-family: inherit;
}

.dp--calendar-header-separator {
    display: none;
}

.dp--calendar-header-item {
    width: 2rem;
    height: 1.75rem;
    text-align: center;
    margin: 0;
    padding: 0;
}

.dp--calendar-item {
    z-index: 1;
}

.dp--calendar-row {
    margin: 0;
}
/* stylelint-enable selector-class-pattern */
</style>
